﻿import React, { useState, useEffect } from 'react';
import GlobalDialog from '../Modal/GlobalDialog';
import { ModalMetadata } from '../../models/modal-metadata';
import { ModalMessageType } from '../../models/enums';
import { Hazard } from '../../models/Common/hazard';
import { PrecautionaryStatement } from '../../models/Common/precautionary-statement';
import { ToastProps } from '../../models/toast-props';
import { CrudFunctions } from '../../utilities/crud-functions';

import DataGrid, {
    Column,
    FilterRow,
    Selection,
    HeaderFilter,
    Toolbar,
    Item as ToolbarItem,
    Paging,
    SearchPanel,
    LoadPanel
} from 'devextreme-react/data-grid';

import List from 'devextreme-react/list';
import Button from 'devextreme-react/button';
import Toast from 'devextreme-react/toast';

//temp
//const versions = [{ versionNumber: 11, year: 2025 }];

const pictogramTypes = [
    { id: 1, name: "hazard-explosive" },
    { id: 2, name: "hazard-flammable" },
    { id: 3, name: "hazard-oxidizing" },
    { id: 4, name: "hazard-compressed-gas" },
    { id: 5, name: "hazard-corrosive" },
    { id: 6, name: "hazard-toxic" },
    { id: 7, name: "hazard-harmful" },
    { id: 8, name: "hazard-health-hazard" },
    { id: 9, name: "hazard-environmental-hazard" },
];

let key = 0;

export interface Props {
    modal?: boolean;
    onSubmit?: (selectedHazards: Hazard[]) => void;
    onClose?: () => void;
}

export default function HazardStatementsComponent(props: Props) {
    const [hazards, setHazards] =
        useState<Hazard[]>();

    //used standalone, and to show precautionary statements
    const [selectedHazard, setSelectedHazard] =
        useState<Hazard>();

    //used for dialog
    const [selectedHazards, setSelectedHazards] =
        useState<Hazard[]>();

    const [precautionaryStatements, setPrecautionaryStatements] =
        useState<PrecautionaryStatement[]>();

    const [globalDialogMetadata, setGlobalDialogMetadata] =
        useState<ModalMetadata>();

    const [globalDialogVisible, setGlobalDialogVisible] =
        useState<boolean>();

    const [toastProps, setToastProps] =
        useState<ToastProps>();

    useEffect(() => {
        getAllData();
    }, []);

    key++;

    return (
        <div className={`pw-content ${props.modal && 'horizontal-margin-auto'}`}>

            {!props.modal &&
                <div className="lq-title-bar pw-form-header">
                    <div className="lq-main-title">Hazards</div>

                    <div className="title-bar-spacer" />
                    <div className="save-toolbar">
                        <Button id="goBackButton" text="" icon="arrowleft" type="normal"
                            hint="go back"
                            height={'2.6em'}
                            onClick={goBack} />
                    </div>
                </div>
            }

            {props.modal &&
                <div className="pw-comp-row pw-margin-top-1">
                    <div>
                        <Button id="applySelectionButton"
                            className="pw-button"
                            text="Apply" type="success"
                            hint="apply selection"
                            height={'2.6em'}
                            onClick={onApplyClick} />

                        <Button id="cancelButton"
                            className="pw-button"
                            text="Cancel" type="normal"
                            hint="cancel"
                            height={'2.6em'}
                            onClick={onCancelClick} />
                    </div>
                </div>
            }

            {(props.modal && selectedHazards) &&
                <div>
                    <div>Selected Hazards:</div>
                    <div className="pw-comp-row">
                        {selectedHazards.map((item, idx) =>
                            <div key={`h-${idx}`} className="pw-form-unit pw-bold" > {item.hCode}</div>
                        )}
                    </div>
                </div>
            }

            <div className="pw-margin-top-05">
                <DataGrid
                    height="20rem" width="100%"
                    dataSource={hazards ? hazards : []}
                    wordWrapEnabled={true}
                    allowColumnResizing={true}
                    columnResizingMode='widget'
                    keyExpr="hazardId"
                    rowAlternationEnabled={true}
                    showRowLines={true}
                    onSelectionChanged={hazardsSelectionChanged}
                    showBorders={true}>

                    <LoadPanel enabled={true} />
                    <Paging enabled={false} />
                    <Selection mode={props.modal ? 'multiple' : 'single'} />
                    <HeaderFilter visible={true} />
                    <FilterRow visible={true} />

                    <SearchPanel visible={true} />

                    <Column dataField='hCode' caption="H-Code" dataType='string' width='5rem' />
                    <Column dataField='statement' cellRender={renderHazardStatement} dataType='string' />
                    <Column dataField='hazardClass' dataType='string' width='10rem' />
                    <Column dataField='category' dataType='string' width="10rem" />
                    <Column dataField='unModelRegulationsClass' dataType='string' width="8rem" />
                    <Column dataField='signalWord' cellRender={renderSignalWord} dataType='string' width="8rem" />

                    <Toolbar>
                        {/*
                        <ToolbarItem location='before'>
                            <SelectBox id="versionsSelectBox" className="selectbox"
                                dataSource={versions}
                                itemRender={renderVersion}
                                value={selectedVersion ? selectedVersion : []} />
                        </ToolbarItem>
                        */}

                        {props.modal &&
                            <ToolbarItem location='before'>
                                <div className="pw-datagrid-toolbar-template">
                                    <div className="pw-datagrid-toobar-title">Hazard Statements</div>
                                </div>
                            </ToolbarItem>
                        }

                        <ToolbarItem location='after' name="searchPanel" />
                    </Toolbar>

                </DataGrid>
            </div>

            <div className="pw-margin-top-1 pw-margin-bottom-1">
                <div>{pictogramCellRender()}</div>
            </div>

            {selectedHazard &&
                <>
                    {(selectedHazard.preventions && selectedHazard.preventions.length > 0) &&
                        <div className="pw-margin-top-1">
                            <div className="pw-form-unit-label">Precautionary: Prevention</div>
                            <div className="pw-form-unit pw-bg-rb-100 pw-default-border">
                                <List height="100%"
                                    selectionMode="none"
                                    dataSource={selectedHazard && selectedHazard.preventions ?
                                        selectedHazard.preventions : []}
                                    itemRender={renderPrecautionaryStatements} />
                            </div>
                        </div>
                    }

                    {(selectedHazard.responses && selectedHazard.responses.length > 0) &&
                        <div className="pw-margin-top-1">
                            <div className="pw-form-unit-label">Precautionary: Response</div>
                            <div className="pw-form-unit pw-bg-rb-100 pw-default-border">
                                <List height="100%"
                                    selectionMode="none"
                                    dataSource={selectedHazard && selectedHazard.responses ?
                                        selectedHazard.responses : []}
                                    itemRender={renderPrecautionaryStatements} />
                            </div>
                        </div>
                    }

                    {(selectedHazard.storages && selectedHazard.storages.length > 0) &&
                        <div className="pw-margin-top-1">
                            <div className="pw-form-unit-label">Precautionary: Storage</div>
                            <div className="pw-form-unit pw-bg-rb-100 pw-default-border">
                                <List height="100%"
                                    selectionMode="none"
                                    dataSource={selectedHazard && selectedHazard.storages ?
                                        selectedHazard.storages : []}
                                    itemRender={renderPrecautionaryStatements} />
                            </div>
                        </div>
                    }

                    {(selectedHazard.disposals && selectedHazard.disposals.length > 0) &&
                        <div className="pw-margin-top-1">
                            <div className="pw-form-unit-label">Precautionary: Disposal</div>
                            <div className="pw-form-unit pw-bg-rb-100 pw-default-border">
                                <List height="100%"
                                    selectionMode="none"
                                    dataSource={selectedHazard && selectedHazard.disposals ?
                                        selectedHazard.disposals : []}
                                    itemRender={renderPrecautionaryStatements} />
                            </div>
                        </div>
                    }
                </>
            }

            {toastProps && toastProps.visible &&
                <Toast
                    visible={toastProps.visible}
                    message={toastProps.message}
                    type={toastProps.toastType}
                    onHiding={onToastHiding}
                    displayTime={600} />
            }

            {globalDialogVisible &&
                <GlobalDialog metadata={globalDialogMetadata} onClose={clearGlobalDialog} />
            }

        </div>
    );

    function onApplyClick() {
        if (props.onSubmit)
            props.onSubmit(selectedHazards);
    }

    function onCancelClick() {
        if (props.onClose)
            props.onClose();
    }

    function hazardsSelectionChanged({ selectedRowKeys, selectedRowsData }: any) {
        //console.log("hazardsSelectionChanged");
        //console.log(selectedRowKeys);
        //console.log(selectedRowsData);

        if (selectedRowsData && selectedRowsData.length > 0) {
            setSelectedHazard(selectedRowsData[selectedRowsData.length - 1]);

            if (props.modal)
                setSelectedHazards(selectedRowsData);
        }
    }

    async function getHazards() {
        await CrudFunctions.getData("common/hazards")
            .then(data => {
                setHazards(data);
            })
            .catch((res) => {
                showMessage(res, ModalMessageType.Error, "Error Retrieving Hazards");
            });
    }

    async function getPrecautionaryStatements() {
        await CrudFunctions.getData("common/precautionarystatements")
            .then(data => {
                setPrecautionaryStatements(data);
            })
            .catch((res) => {
                showMessage(res, ModalMessageType.Error, "Error Retrieving Technology List");
            });
    }

    async function getAllData() {
        if (!hazards) {
            await Promise.all([
                getHazards(),
                getPrecautionaryStatements()
            ]);
        }
    }

    //render

    function renderHazardStatement(e: any) {
        if (e.data) {
            if (e.data.obsolete) {
                return <div><span className="pw-red">{"(Obsolete) "}</span><span>{e.data.statement}</span></div>
            }
            else {
                return <div>{e.data.statement}</div>
            }
        }
        else
            return <div></div>;
    }

    function renderSignalWord(e: any) {
        if (e.data) {
            let css = '';
            if (e.data.signalWord === "Danger")
                css = "pw-red";
            else if (e.data.signalWord === "Warning")
                css = "pw-blue";

            return <div className={css}>{e.data.signalWord}</div>
        }
        else
            return <div></div>;
    }

    function pictogramCellRender() {
        if (selectedHazard && selectedHazard.pictograms) {
            return (
                <div className="pw-comp-row"> {
                    selectedHazard.pictograms.map((item, i) =>
                        <div key={i.toString()} className="pw-form-unit">
                            <div className={`${pictogramTypes.find(pt => pt.id === item).name}`} style={{ width: "5rem", height: "5rem" }}></div>
                        </div>
                    )}
                </div>
            );
        }
        else
            return <div></div>;
    }

    function renderPrecautionaryStatements(e: any) {
        if (e && precautionaryStatements) {
            const pcStatement = precautionaryStatements.find(s => s.precautionaryCode === e.precautionaryCode);

            return (
                <div><span className="pw-bold">{`${e.precautionaryCode}: `}</span><span className="pw-margin-left-1">{pcStatement ? pcStatement.statement : 'NA'}</span></div>
            );
        }
        else
            return <div></div>;
    }

    function renderVersion(e: any) {
        console.log("renderVersion");
        console.log(e);

        if (e)
            return <div>{`Version #: ${e.versionNumber}, Year: ${e.year}`}</div>;
        else
            return <div></div>;
    }

    //navigation

    function goBack() {
        history.back();
    }

    function showToast(message: string, toastType: string) {
        let toastParams: ToastProps = {
            message: message,
            toastType: toastType,
            visible: true
        };

        setToastProps(toastParams);
    }

    function onToastHiding() {
        let toastParams: ToastProps = {
            message: undefined,
            toastType: undefined,
            visible: false
        };

        setToastProps(toastParams);
    }

    function showMessage(res: any, mode: ModalMessageType, title: string) {
        let mm: ModalMetadata = {
            error: res,
            mode: mode,
            title: title
        };

        setGlobalDialogMetadata(mm);
        setGlobalDialogVisible(true);
    }

    function clearGlobalDialog() {
        setGlobalDialogMetadata(null);
        setGlobalDialogVisible(false);
    }

}